import { Fragment, useEffect, useRef, useState } from "react";
import { Plus, Search, Star, X } from "lucide-react";
import { apiClient } from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { groupOptions, isGrouped } from "@/shared/components/group-options";
import { useDismiss } from "@/shared/hooks/useDismiss";
import { highlightTitle, type CatalogRef, type Mention } from "@/shared/types/mention";

export type CatalogKind = "themes" | "keyMessages" | "spokespersons";

/** Uma opção do catálogo da marca. `group` = título sob o qual aparece
 *  (tema -> grupo de tema); as outras dimensões não têm. */
export type CatalogOption = { id: number; name: string; group?: string | null };

/** O catálogo de UMA marca, nas três dimensões editáveis no card. */
export type MentionCatalog = Record<CatalogKind, CatalogOption[]>;

/** A chave do patch da correção por dimensão — a mesma rota do editor. */
const PATCH_KEY = {
  themes: "themeIds",
  keyMessages: "keyMessageIds",
  spokespersons: "spokespersonIds"
} as const;

const COPY: Record<CatalogKind, { empty: string; add: string; scoreNote: string | null }> = {
  themes: { empty: "Sem subtema", add: "Adicionar subtema", scoreNote: null },
  keyMessages: {
    empty: "Nenhuma",
    add: "Adicionar mensagem-chave",
    scoreNote: "Mexer aqui recalcula a nota (CENA) da menção"
  },
  spokespersons: {
    empty: "Nenhuma",
    add: "Adicionar porta-voz",
    scoreNote: "Mexer aqui recalcula a nota (CENA) da menção"
  }
};

const SEARCH_THRESHOLD = 8;

/**
 * Tema, mensagem-chave ou porta-voz da menção editáveis NA LINHA
 * (2026-10-18, decisão da dona): o × tira na hora e o "+" abre uma lista
 * ancorada com o que a menção ainda não tem. Grava pela mesma rota do
 * editor (`updateMentionClassification`), mandando a lista inteira — o
 * servidor compara e só registra o que mudou. Otimista como as carinhas:
 * o chip muda na hora e volta se a gravação falhar.
 *
 * Quando abre, o catálogo vem de `loadOptions` (o pai guarda a promessa,
 * então 25 cards abrindo o "+" não disparam 25 cargas). Chama-se uma vez
 * por ABERTURA até dar certo: sucesso fica no estado do componente,
 * falha tenta de novo na abertura seguinte. Falha de catálogo aparece
 * dentro do pop-up e nunca vira erro do card.
 */
export function MentionInlineCatalog({
  kind,
  mention,
  token,
  items,
  loadOptions,
  onSaved,
  onError,
  locked: discardedLock,
  readOnly = false
}: {
  kind: CatalogKind;
  mention: Mention;
  token: string;
  items: CatalogRef[];
  loadOptions: () => Promise<CatalogOption[]>;
  onSaved: () => void;
  onError: (message: string) => void;
  /** Menção descartada: nada se edita até restaurar. */
  locked: boolean;
  /** Quem só vê (o Visualizador, 2026-10-22): nem × nem "+". */
  readOnly?: boolean;
}) {
  const locked = discardedLock || readOnly;
  const copy = COPY[kind];
  const [optimistic, setOptimistic] = useState<CatalogRef[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState<
    { status: "idle" | "loading" | "failed" } | { status: "ready"; options: CatalogOption[] }
  >({ status: "idle" });
  const [search, setSearch] = useState("");
  const [alignRight, setAlignRight] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // A lista abre por cima do que vem depois e pode passar da borda de
  // quem rola — no drawer (aberto pela visão Publicações) ela sumia atrás
  // do rodapé, e no card perto do fim da tela. Ao abrir, e de novo quando
  // o catálogo chega e ela cresce, rola só o necessário pra ela caber.
  // `?.` porque o jsdom não tem `scrollIntoView`.
  useEffect(() => {
    if (!open) return;
    popoverRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [open, catalog.status]);

  // Dado novo do servidor: o otimista sai.
  useEffect(() => {
    setOptimistic(null);
  }, [items]);

  useDismiss(open, () => setOpen(false), { ref: wrapperRef });

  const current = optimistic ?? items;

  async function save(
    next: CatalogRef[],
    patch: Parameters<typeof apiClient.updateMentionClassification>[2] = {
      [PATCH_KEY[kind]]: next.map(item => item.id)
    }
  ) {
    setOptimistic(next);
    setBusy(true);
    try {
      await apiClient.updateMentionClassification(token, mention.id, patch);
      onSaved();
    } catch (failure) {
      setOptimistic(null);
      onError(errorMessage(failure, "Não foi possível gravar a alteração."));
    } finally {
      setBusy(false);
    }
  }

  /**
   * A estrela do chip de tema (2026-10-19): escolhe o tema em DESTAQUE.
   * Vazia fixa este; cheia de escolha MANUAL desfaz (`null` volta à
   * sugestão do servidor); cheia de sugestão AUTO fixa a escolha. Na tela a
   * estrela muda na hora; ao desfazer, a sugestão nova só aparece com a
   * recarga.
   */
  function toggleHighlight(item: CatalogRef) {
    const clearing = item.highlight === "manual";
    void save(
      current.map(other => ({
        ...other,
        highlight: clearing ? null : other.id === item.id ? "manual" : null
      })),
      { highlightThemeId: clearing ? null : item.id }
    );
  }

  function toggleOpen() {
    if (open) {
      setOpen(false);
      return;
    }
    setSearch("");
    // Sem espaço à direita do botão, a lista abre alinhada à direita.
    const trigger = wrapperRef.current?.getBoundingClientRect();
    const card = wrapperRef.current?.closest(".mention-card")?.getBoundingClientRect();
    setAlignRight(Boolean(trigger && card && card.width > 0 && trigger.left + 300 > card.right));
    setOpen(true);
    if (catalog.status === "ready" || catalog.status === "loading") return;
    setCatalog({ status: "loading" });
    loadOptions().then(
      options => setCatalog({ status: "ready", options }),
      () => setCatalog({ status: "failed" })
    );
  }

  function choose(option: CatalogOption) {
    setOpen(false);
    void save([
      ...current,
      { id: option.id, name: option.name, group_name: option.group ?? null }
    ]);
  }

  const available =
    catalog.status === "ready"
      ? catalog.options.filter(option => !current.some(item => item.id === option.id))
      : [];
  const blocks = groupOptions(available, search, {
    labelOf: option => option.name,
    groupOf: option => option.group
  });
  const grouped = isGrouped(available, option => option.group);
  const lockedTitle = locked ? "Restaure a menção pra editar" : undefined;

  return (
    <div className="mc-chips">
      {current.length ? (
        current.map(item => (
          <span
            className={`mc-chip${item.highlight ? " is-highlight" : ""}`}
            key={item.id}
            title={chipTitle(kind, item)}
          >
            {kind === "themes" && !locked && (
              <button
                type="button"
                className={`mc-chip-star${item.highlight ? " is-on" : ""}`}
                aria-label={starLabel(item)}
                title={starLabel(item)}
                disabled={busy}
                onClick={() => toggleHighlight(item)}
              >
                <Star size={12} aria-hidden="true" />
              </button>
            )}
            {kind === "themes" && locked && item.highlight && <Star size={11} aria-hidden="true" />}
            {kind === "themes" && item.group_name && (
              <span className="mention-theme-group">{item.group_name}</span>
            )}
            {kind === "themes" ? (
              <span className="mention-theme-name">{item.name}</span>
            ) : (
              item.name
            )}
            {!locked && (
              <button
                type="button"
                className="mc-chip-remove"
                aria-label={`Tirar ${item.name}`}
                disabled={busy}
                onClick={() => void save(current.filter(other => other.id !== item.id))}
              >
                <X size={11} aria-hidden="true" />
              </button>
            )}
          </span>
        ))
      ) : (
        <span className="mc-empty">{copy.empty}</span>
      )}
      {!readOnly && <div className="mc-add" ref={wrapperRef}>
        <button
          type="button"
          className="mc-chip mc-chip-add"
          aria-label={copy.add}
          aria-expanded={open}
          aria-haspopup="listbox"
          title={lockedTitle ?? copy.scoreNote ?? copy.add}
          disabled={locked || busy}
          onClick={toggleOpen}
        >
          <Plus size={12} aria-hidden="true" />
        </button>
        {open && (
          <div
            ref={popoverRef}
            className={`mc-popover${alignRight ? " is-right" : ""}`}
            role="dialog"
            aria-label={copy.add}
          >
            {catalog.status === "loading" && <p className="multi-filter-empty">Carregando…</p>}
            {catalog.status === "failed" && (
              <p className="multi-filter-empty">Não foi possível carregar o catálogo</p>
            )}
            {catalog.status === "ready" && (
              <>
                {available.length > SEARCH_THRESHOLD && (
                  <div className="multi-filter-search">
                    <Search size={15} aria-hidden="true" />
                    <input
                      type="search"
                      value={search}
                      onChange={event => setSearch(event.target.value)}
                      placeholder="Buscar…"
                      aria-label="Buscar opções"
                      autoComplete="off"
                      spellCheck={false}
                      autoFocus
                    />
                  </div>
                )}
                <div className="multi-filter-options filter-chip-group">
                  {blocks.map(block => (
                    <Fragment key={block.group ?? "\u0000"}>
                      {grouped && (
                        <h5 className="multi-filter-group-title">{block.group ?? "Sem tema"}</h5>
                      )}
                      {block.options.map(option => (
                        <button
                          key={option.id}
                          type="button"
                          className="filter-chip"
                          disabled={busy}
                          onClick={() => choose(option)}
                        >
                          {option.name}
                        </button>
                      ))}
                    </Fragment>
                  ))}
                  {!blocks.length && (
                    <p className="multi-filter-empty">
                      {available.length ? "Nenhuma opção encontrada." : "Nada mais a adicionar."}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>}
    </div>
  );
}

function starLabel(item: CatalogRef): string {
  if (item.highlight === "manual") return "Tirar o destaque (volta à sugestão)";
  if (item.highlight === "auto") return "Fixar como destaque";
  return "Escolher como subtema em destaque";
}

function chipTitle(kind: CatalogKind, item: CatalogRef): string {
  if (kind === "themes") {
    return [item.group_name ? `${item.group_name} · ${item.name}` : item.name, highlightTitle(item)]
      .filter(Boolean)
      .join(" — ");
  }
  if (kind === "keyMessages" && item.source === "imported") return "Importada da planilha";
  return item.name;
}
